// /////////////////////////////////////////////////////////////
// [ファイル] BuildingDetailPage.tsx
// [概要] 建物詳細の全画面ページ。
//        URL /crm/customers/building/:id でアクセス (CrmCustomerLayout 配下)。
//        基本情報 + 関係者 (所有 / 居住 / 入居) + 登記物件リンクを表示し、
//        建物編集 (BuildingForm) と関係者の追加・編集・削除を行う。
//        編集成功時は親 Layout の建物一覧 (building.refresh) も最新化する。
// /////////////////////////////////////////////////////////////
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Grid,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import * as Icons from '../constants/icons';
import LoadingView from '../components/LoadingView';
import BuildingForm from '../components/BuildingForm';
import { useCrmTableContext } from '../providers/CrmTableProvider';
import MinimalPartyForm, { RegisteredParty } from '../components/MinimalPartyForm';
import { useAuth } from '../firebase/AuthProvider';
import { useNotification } from '../providers/NotificationProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_TEXT_BUTTON_SX,
} from '../constants/buttonStyles';
import { ep } from '../api/endpoints';

const DETAIL_URL = ep('get_crm_building_detail');
const UPSERT_RELATION_URL = ep('upsert_building_relation');
const DELETE_RELATION_URL = ep('delete_building_relation');
const SET_PRIMARY_URL = ep('set_primary_building_relation');
const DELETE_BUILDING_URL = ep('delete_crm_building');
const SEARCH_CUSTOMERS_URL = ep('search_crm_customers');

// 関係区分のコードと表示名 (DB には英字コードで保存)
const RELATION_TYPE_LABELS: Record<string, string> = {
  owner: '所有者',
  resident: '居住',
  tenant: '入居 (テナント)',
  other: 'その他',
};

const LINK_KIND_LABELS: Record<string, string> = {
  building: '一棟 (建物全部事項)',
  land: '土地',
  unit: '区分建物',
};

// ラベル + 値の表示コンポーネント (詳細表示用)
const FieldDisplay: React.FC<{ label: string; value: any; size?: number; multiline?: boolean }> = ({
  label,
  value,
  size = 3,
  multiline = false,
}) => (
  <Grid size={size}>
    <Box sx={{ pb: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mt: 0.25,
          minHeight: 20,
          whiteSpace: multiline ? 'pre-wrap' : 'normal',
          color: value ? 'text.primary' : 'text.disabled',
        }}
      >
        {value || '-'}
      </Typography>
    </Box>
  </Grid>
);

// ---------------------------------------------------------------------------
// 関係者 追加/編集ダイアログ
// ---------------------------------------------------------------------------
interface RelationDialogProps {
  open: boolean;
  buildingId: string;
  relation: any | null; // null=新規
  defaultType?: string;
  // 最小登録ダイアログの住所初期値に使う建物情報
  building: { postal_code?: string; address_main?: string } | null;
  onClose: () => void;
  onSaved: () => void;
}

function RelationDialog({ open, buildingId, relation, defaultType, building, onClose, onSaved }: RelationDialogProps) {
  const { user } = useAuth();
  const notify = useNotification();

  const [relationType, setRelationType] = useState('owner');
  // 絶対登録関係の方針によりフリーテキスト入力は廃止。関係者は顧客マスタ選択のみ
  // (未登録者は MinimalPartyForm で顧客マスタへ最小登録してから紐付ける)
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
  const [customerOptions, setCustomerOptions] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [minimalOpen, setMinimalOpen] = useState(false);
  const [roomNo, setRoomNo] = useState('');
  const [ownershipShare, setOwnershipShare] = useState('');
  const [isCurrent, setIsCurrent] = useState(true);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (relation) {
      setRelationType(relation.relation_type || 'owner');
      setSelectedCustomer(
        relation.customer_id
          ? { id: relation.customer_id, display_name: relation.display_name, customer_type: relation.customer_type }
          : null
      );
      setRoomNo(relation.room_no || '');
      setOwnershipShare(relation.ownership_share || '');
      setIsCurrent(relation.is_current !== false);
      setNotes(relation.notes || '');
    } else {
      setRelationType(defaultType || 'owner');
      setSelectedCustomer(null);
      setRoomNo('');
      setOwnershipShare('');
      setIsCurrent(true);
      setNotes('');
    }
    setCustomerOptions([]);
    setMinimalOpen(false);
  }, [open, relation, defaultType]);

  // 顧客の横断検索 (SFA 取引先選択と同じ search_crm_customers を再利用)
  const handleCustomerSearch = async (query: string) => {
    if (!query.trim()) {
      setCustomerOptions([]);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await axios.post(SEARCH_CUSTOMERS_URL, { query });
      if (res.data.status === 'success') {
        setCustomerOptions(res.data.results || []);
      }
    } catch (e) {
      console.error('customer search error:', e);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedCustomer) {
      notify.error('関係者となる顧客を選択してください（未登録の場合は最小登録で作成できます）');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(UPSERT_RELATION_URL, {
        buildingId,
        relationId: relation?.id || null,
        data: {
          customer_id: selectedCustomer?.id,
          owner_name: null,
          owner_address: null,
          relation_type: relationType,
          room_no: roomNo.trim(),
          ownership_share: ownershipShare.trim(),
          is_current: isCurrent,
          notes,
          operator_id: user?.email,
        },
      });
      if (res.data.status === 'success') {
        notify.success(res.data.message || '保存しました');
        onSaved();
      } else {
        notify.error(res.data.message || '保存に失敗しました');
      }
    } catch (e) {
      console.error('relation save error:', e);
      notify.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: '0.875rem',
          fontWeight: 700,
          py: 0.75,
          bgcolor: '#f1f5f9',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Icons.Handshake sx={{ fontSize: 18, color: blueGrey[500] }} />
        {relation ? '関係者の編集' : '関係者の追加'}
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          select
          size="small"
          label="関係区分"
          value={relationType}
          onChange={(e) => setRelationType(e.target.value)}
          sx={{ width: 220 }}
        >
          {Object.entries(RELATION_TYPE_LABELS).map(([code, label]) => (
            <MenuItem key={code} value={code}>{label}</MenuItem>
          ))}
        </TextField>

        <Box>
          <Autocomplete
              options={customerOptions}
              size="small"
              fullWidth
              loading={searchLoading}
              filterOptions={(x) => x}
              noOptionsText="氏名・会社名で検索してください"
              getOptionLabel={(option: any) => option.display_name || ''}
              isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
              value={selectedCustomer}
              onChange={(_, newValue) => setSelectedCustomer(newValue)}
              onInputChange={(_, newInput, reason) => {
                if (reason === 'input') handleCustomerSearch(newInput);
              }}
              renderOption={(props, option: any) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {option.customer_type === 'individual'
                      ? <Icons.Person sx={{ fontSize: 16, color: blueGrey[400] }} />
                      : <Icons.Business sx={{ fontSize: 16, color: '#1976d2' }} />}
                    {option.display_name}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="顧客 (個人・法人)" placeholder="氏名・会社名で検索" />
              )}
            />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              未登録の関係者は顧客マスタへ最小登録してから紐付けます
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Icons.PersonAdd sx={{ fontSize: 15 }} />}
              onClick={() => setMinimalOpen(true)}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              新規関係先を登録
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField
            size="small"
            label="号室・階"
            value={roomNo}
            onChange={(e) => setRoomNo(e.target.value)}
            placeholder="例: 302"
            sx={{ width: 160 }}
          />
          <TextField
            size="small"
            label="持分"
            value={ownershipShare}
            onChange={(e) => setOwnershipShare(e.target.value)}
            placeholder="例: 2分の1"
            sx={{ width: 160 }}
          />
          <FormControlLabel
            control={<Checkbox checked={isCurrent} onChange={(e) => setIsCurrent(e.target.checked)} size="small" />}
            label={<Typography variant="body2">現在有効</Typography>}
          />
        </Box>

        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label="備考"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={STANDARD_TEXT_BUTTON_SX} disabled={saving}>
          キャンセル
        </Button>
        <Button
          onClick={handleSave}
          startIcon={<Icons.Save />}
          sx={STANDARD_CTA_BUTTON_SX}
          disabled={saving}
        >
          {saving ? '保存中...' : '保存する'}
        </Button>
      </DialogActions>

      <MinimalPartyForm
        open={minimalOpen}
        building={building}
        onClose={() => setMinimalOpen(false)}
        onRegistered={(party: RegisteredParty) => {
          setSelectedCustomer({ id: party.id, display_name: party.display_name, customer_type: party.customer_type });
          setMinimalOpen(false);
        }}
      />
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// 建物詳細ページ本体
// ---------------------------------------------------------------------------
export default function BuildingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { building } = useCrmTableContext();
  const notify = useNotification();
  const confirm = useConfirm();

  const [detail, setDetail] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [relationDialog, setRelationDialog] = useState<{
    open: boolean;
    relation: any | null;
    defaultType?: string;
  }>({ open: false, relation: null });

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    try {
      const res = await axios.post(DETAIL_URL, { buildingId: id });
      if (res.data.status === 'success') {
        setDetail(res.data.detail);
      } else {
        notify.error('建物情報の取得に失敗しました');
      }
    } catch (e) {
      console.error('BuildingDetailPage fetch error:', e);
      notify.error('建物情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
    // notify は Provider 再レンダーごとに識別子が変わるため依存に含めない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setLoading(true);
    fetchDetail();
  }, [fetchDetail]);

  // 一覧から渡された URL Query (?tab=building 等) を保持して戻る
  const handleBackToList = () => {
    const state = location.state as { listSearch?: string } | null;
    const searchPart = state?.listSearch || '?tab=building';
    navigate(`/crm/customers${searchPart}`);
  };

  // メイン住居の手動切替 (旧メインはサーバー側で自動解除される)
  const handleSetPrimary = async (relation: any) => {
    try {
      const res = await axios.post(SET_PRIMARY_URL, {
        customerId: relation.customer_id,
        relationId: relation.id,
      });
      if (res.data.status === 'success') {
        notify.success(res.data.message || 'メイン住居を切り替えました');
        fetchDetail();
      } else {
        notify.error(res.data.message || '切り替えに失敗しました');
      }
    } catch (e) {
      console.error('set primary error:', e);
      notify.error('切り替えに失敗しました');
    }
  };

  // 建物マスタの削除 (関係者・登記・SFA 案件の参照が残っていればサーバー側で拒否される)
  const handleDeleteBuilding = async () => {
    const ok = await confirm({
      title: '建物の削除',
      message: `「${detail?.building_name || ''}」を削除しますか？\nこの操作は取り消せません。関係者・登記・SFA 案件が紐付いている場合は削除できません。`,
      confirmText: '削除する',
      destructive: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await axios.post(DELETE_BUILDING_URL, { buildingId: id });
      if (res.data.status === 'success') {
        notify.success(res.data.message || '建物を削除しました');
        building.refresh();
        handleBackToList();
      } else {
        notify.error(res.data.message || '削除に失敗しました');
      }
    } catch (e: any) {
      console.error('building delete error:', e);
      notify.error(e?.response?.data?.message || '削除に失敗しました');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteRelation = async (relation: any) => {
    const ok = await confirm({
      title: '関係者の削除',
      message: `「${relation.display_name || relation.owner_name || ''}」との関係情報を削除しますか？`,
      confirmText: '削除する',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await axios.post(DELETE_RELATION_URL, {
        buildingId: id,
        relationId: relation.id,
      });
      if (res.data.status === 'success') {
        notify.success(res.data.message || '削除しました');
        fetchDetail();
        building.refresh();
      } else {
        notify.error(res.data.message || '削除に失敗しました');
      }
    } catch (e) {
      console.error('relation delete error:', e);
      notify.error('削除に失敗しました');
    }
  };

  // 関係者を区分ごとにグループ化して表示する
  const relationGroups = useMemo(() => {
    const relations: any[] = detail?.relations || [];
    return [
      { key: 'owner', title: '所有者', icon: <Icons.RealEstateAgent sx={{ fontSize: 18 }} />, items: relations.filter(r => r.relation_type === 'owner') },
      { key: 'resident', title: '居住・入居者', icon: <Icons.Groups sx={{ fontSize: 18 }} />, items: relations.filter(r => r.relation_type === 'resident' || r.relation_type === 'tenant') },
      { key: 'other', title: 'その他の関係者', icon: <Icons.Handshake sx={{ fontSize: 18 }} />, items: relations.filter(r => r.relation_type === 'other') },
    ];
  }, [detail]);

  if (loading) return <LoadingView message="建物情報を取得中..." height="80vh" />;

  if (!detail) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">建物が見つかりませんでした</Typography>
        <Button onClick={handleBackToList} sx={{ ...STANDARD_TEXT_BUTTON_SX, mt: 2 }}>
          一覧へ戻る
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, width: '95%', maxWidth: 1100, margin: '0 auto', boxSizing: 'border-box' }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <IconButton onClick={handleBackToList} size="small">
          <Icons.ArrowBack />
        </IconButton>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            bgcolor: '#e8f5e9',
            color: '#2e7d32',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icons.HomeWork sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: blueGrey[700], lineHeight: 1.3 }} noWrap>
              {detail.building_name}
            </Typography>
            {detail.registration_source === 'customer_auto' && (
              <Chip
                label="簡易登録"
                size="small"
                sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#fff3e0', color: '#e65100', flexShrink: 0 }}
              />
            )}
          </Box>
          {detail.building_name_kana && (
            <Typography variant="caption" color="text.disabled">{detail.building_name_kana}</Typography>
          )}
        </Box>
        <Button
          onClick={handleDeleteBuilding}
          startIcon={<Icons.Delete />}
          disabled={deleting}
          sx={{ ...STANDARD_TEXT_BUTTON_SX, color: '#c62828' }}
        >
          {deleting ? '削除中...' : '建物を削除'}
        </Button>
        <Button
          onClick={() => setIsEditOpen(true)}
          startIcon={<Icons.Edit />}
          sx={STANDARD_CTA_BUTTON_SX}
        >
          編集する
        </Button>
      </Box>

      {/* 基本情報 */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75, color: blueGrey[600] }}>
          <Icons.InfoOutlined sx={{ fontSize: 18 }} /> 基本情報
        </Typography>
        <Grid container spacing={1.5}>
          <FieldDisplay label="郵便番号" value={detail.postal_code} size={3} />
          <FieldDisplay label="住所" value={detail.address_main} size={9} />
          <FieldDisplay label="種別" value={detail.building_type} size={3} />
          <FieldDisplay label="構造" value={detail.structure} size={5} />
          <FieldDisplay label="階数" value={detail.total_floors != null ? `${detail.total_floors}階` : ''} size={2} />
          <FieldDisplay label="築年月" value={detail.built_year_month} size={2} />
          <FieldDisplay label="最終更新" value={`${(detail.updated_at || '').substring(0, 16).replace('T', ' ')}${(detail.updated_operator_name || '').trim() ? ` (${detail.updated_operator_name})` : ''}`} size={6} />
          <FieldDisplay label="備考" value={detail.notes} size={12} multiline />
        </Grid>
      </Paper>

      {/* 関係者 (所有 / 居住・入居 / その他) */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75, color: blueGrey[600] }}>
            <Icons.People sx={{ fontSize: 18 }} /> 関係者
          </Typography>
          <Button
            size="small"
            startIcon={<Icons.PersonAdd sx={{ fontSize: 16 }} />}
            onClick={() => setRelationDialog({ open: true, relation: null, defaultType: 'owner' })}
            sx={STANDARD_TEXT_BUTTON_SX}
          >
            関係者を追加
          </Button>
        </Box>

        {relationGroups.every(g => g.items.length === 0) ? (
          <Typography variant="body2" color="text.disabled" sx={{ py: 1.5, textAlign: 'center' }}>
            関係者が登録されていません。「関係者を追加」から所有者・居住者を登録してください。
          </Typography>
        ) : (
          relationGroups.filter(g => g.items.length > 0).map((group, gi) => (
            <Box key={group.key} sx={{ mb: gi < relationGroups.length - 1 ? 1.5 : 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                {group.icon} {group.title}
              </Typography>
              <Stack divider={<Divider flexItem />} sx={{ border: '1px solid #eee', borderRadius: 1.5 }}>
                {group.items.map((rel: any) => (
                  <Box key={rel.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1 }}>
                    {rel.customer_type === 'individual'
                      ? <Icons.Person sx={{ fontSize: 18, color: blueGrey[400], flexShrink: 0 }} />
                      : rel.customer_type === 'corporate'
                        ? <Icons.Business sx={{ fontSize: 18, color: '#1976d2', flexShrink: 0 }} />
                        : <Icons.PersonOutline sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        {rel.customer_id ? (
                          <Typography
                            variant="body2"
                            noWrap
                            onClick={() => navigate(`/crm/customers/${rel.customer_id}`)}
                            sx={{
                              fontWeight: 700,
                              cursor: 'pointer',
                              '&:hover': { color: '#1976d2', textDecoration: 'underline' },
                            }}
                          >
                            {rel.display_name || '-'}
                          </Typography>
                        ) : (
                          <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
                            {rel.display_name || rel.owner_name || '-'}
                          </Typography>
                        )}
                        {!rel.customer_id && (
                          <Chip label="顧客未登録" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#fff3e0', color: '#e65100' }} />
                        )}
                        {rel.is_primary && (
                          <Chip label="メイン住居" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#e8eaf6', color: '#3949ab', fontWeight: 600 }} />
                        )}
                        {rel.relation_type === 'tenant' && (
                          <Chip label="テナント" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#e3f2fd', color: '#1976d2' }} />
                        )}
                        {rel.is_current === false && (
                          <Chip label="過去" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#eceff1', color: 'text.disabled' }} />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.25 }}>
                        {rel.room_no && (
                          <Typography variant="caption" color="text.secondary">
                            <Box component="span" sx={{ color: 'text.disabled' }}>号室 </Box>{rel.room_no}
                          </Typography>
                        )}
                        {rel.ownership_share && (
                          <Typography variant="caption" color="text.secondary">
                            <Box component="span" sx={{ color: 'text.disabled' }}>持分 </Box>{rel.ownership_share}
                          </Typography>
                        )}
                        {rel.party_address && (
                          <Typography variant="caption" color="text.disabled" noWrap>
                            {rel.party_address}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {rel.customer_id && !rel.is_primary && rel.is_current !== false
                      && (rel.relation_type === 'resident' || rel.relation_type === 'tenant') && (
                      <Button
                        size="small"
                        onClick={() => handleSetPrimary(rel)}
                        sx={{ textTransform: 'none', fontSize: '0.7rem', flexShrink: 0, color: '#3949ab' }}
                      >
                        メインに設定
                      </Button>
                    )}
                    <IconButton size="small" onClick={() => setRelationDialog({ open: true, relation: rel })}>
                      <Icons.Edit sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteRelation(rel)}>
                      <Icons.Delete sx={{ fontSize: 16, color: '#ef5350' }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Box>
          ))
        )}
      </Paper>

      {/* 登記物件リンク */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75, color: blueGrey[600] }}>
          <Icons.Landscape sx={{ fontSize: 18 }} /> 登記物件
        </Typography>
        {(detail.touki_links || []).length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ py: 1, textAlign: 'center' }}>
            紐付けられた登記物件はありません (登記簿検索からの紐付けはフェーズ2で対応予定)
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />} sx={{ border: '1px solid #eee', borderRadius: 1.5 }}>
            {(detail.touki_links || []).map((link: any) => (
              <Box key={link.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1 }}>
                <Chip
                  label={LINK_KIND_LABELS[link.link_kind] || link.link_kind}
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#e8f5e9', color: '#2e7d32', flexShrink: 0 }}
                />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                    {link.location || link.property_key}
                    {link.kaoku_no && ` 家屋番号 ${link.kaoku_no}`}
                    {link.chiban && ` 地番 ${link.chiban}`}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.25 }}>
                    {link.current_owner_name && (
                      <Typography variant="caption" color="text.secondary">
                        <Box component="span" sx={{ color: 'text.disabled' }}>現所有者 </Box>{link.current_owner_name}
                      </Typography>
                    )}
                    {link.has_lien && (
                      <Chip label="担保権あり" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#ffebee', color: '#c62828' }} />
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {/* 関連案件 (この建物を対象物件とする SFA 案件、監視コックピットの一部) */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75, color: blueGrey[600] }}>
          <Icons.FolderOpen sx={{ fontSize: 18 }} /> 関連案件
        </Typography>
        {(detail.projects || []).length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ py: 1, textAlign: 'center' }}>
            リンクされた案件はありません (案件詳細の「対象建物」から追加できます)
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />} sx={{ border: '1px solid #eee', borderRadius: 1.5 }}>
            {(detail.projects || []).map((prj: any) => (
              <Box key={prj.link_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1 }}>
                <Box
                  sx={{ flexGrow: 1, minWidth: 0, cursor: 'pointer', '&:hover': { color: '#1976d2' } }}
                  onClick={() => navigate(`/salesprojectsmanagement/${prj.project_id}`)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flexWrap: 'wrap' }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                      {prj.project_name || prj.project_id}
                    </Typography>
                    {prj.project_type && (
                      <Chip
                        label={prj.project_type === 'purchase' ? '仕入' : prj.project_type === 'sales' ? '売上' : prj.project_type}
                        size="small"
                        sx={{ height: 18, fontSize: '0.65rem',
                              bgcolor: prj.project_type === 'purchase' ? '#e3f2fd' : '#fce4ec',
                              color: prj.project_type === 'purchase' ? '#1976d2' : '#c2185b' }}
                      />
                    )}
                    {prj.phase && (
                      <Chip label={prj.phase} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#f5f5f5', color: 'text.secondary' }} />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.25 }}>
                    {prj.client_name && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        <Box component="span" sx={{ color: 'text.disabled' }}>取引先 </Box>{prj.client_name}
                      </Typography>
                    )}
                    {prj.main_staff_name && (
                      <Typography variant="caption" color="text.disabled" noWrap>
                        担当 {prj.main_staff_name}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Icons.ChevronRight sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {/* 建物編集ダイアログ */}
      {isEditOpen && (
        <BuildingForm
          buildingId={id}
          open={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          onSuccess={() => {
            setIsEditOpen(false);
            fetchDetail();
            building.refresh();
          }}
        />
      )}

      {/* 関係者 追加/編集ダイアログ */}
      {relationDialog.open && id && (
        <RelationDialog
          building={detail ? { postal_code: detail.postal_code, address_main: detail.address_main } : null}
          open={relationDialog.open}
          buildingId={id}
          relation={relationDialog.relation}
          defaultType={relationDialog.defaultType}
          onClose={() => setRelationDialog({ open: false, relation: null })}
          onSaved={() => {
            setRelationDialog({ open: false, relation: null });
            fetchDetail();
            building.refresh();
          }}
        />
      )}
    </Box>
  );
}
